// Copyright (c) 2024 Goffy. This file belongs to Goffy. All Rights Reserved.

import { ScrollView, StyleSheet, Pressable, Platform } from 'react-native';
import { useEffect, useMemo, useState } from 'react';
import { Redirect, useLocalSearchParams, useRouter, useRootNavigationState } from 'expo-router';
import { formatHoleDistance } from '@/features/units/distance';
import { useDistanceUnit } from '@/stores/distanceUnitState';

import { Text, View } from '@/components/Themed';
import { useI18n } from '@/i18n';
import { useUser } from './user-context';
import { CourseAnalysisStatusPanel } from '@/features/course-trace/CourseAnalysisStatusPanel';
import { useEffectiveRoles } from '@/hooks/useEffectiveRoles';
import { getApiBaseUrl } from '@/services/apiBaseUrl';
import appColors from '@/theme/colors';

type Hole = {
  id: string;
  courseId: string;
  number: number;
  par: number;
  strokeIndex: number;
  /** The length a player may be shown (CLAUDE.md §19); null when we have not measured the hole. */
  displayLengthMeters?: number | null;
};

type CourseDetail = {
  id: string;
  name: string;
  address: string;
  lat: number;
  lon: number;
  source?: string;
  createdAt?: string;
  updatedAt?: string;
  holes?: Hole[];
};

export default function CourseDetailsScreen() {
  const distanceUnit = useDistanceUnit();
  const { t } = useI18n();
  const { courseId } = useLocalSearchParams<{ courseId?: string }>();
  const router = useRouter();
  const navState = useRootNavigationState();
  const isNavigationReady = !!navState?.key;
  const { user } = useUser();
  const { isAdmin } = useEffectiveRoles(user);
  const apiBaseUrl = useMemo(() => getApiBaseUrl(), []);
  const [course, setCourse] = useState<CourseDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchCourse = async () => {
      if (!courseId) {
        setError(t('courseDetails.errorMissingId'));
        return;
      }
      setLoading(true);
      setError(null);
      try {
        const response = await fetch(`${apiBaseUrl}/api/geodata/courses/${courseId}`, {
          headers: user?.token ? { Authorization: `Bearer ${user.token}` } : undefined,
        });
        if (response.status === 404) {
          setError(t('courseDetails.errorNotFound'));
          return;
        }
        if (!response.ok) {
          const message = await response.text();
          throw new Error(message || t('courseDetails.errorLoad'));
        }
        const payload = (await response.json()) as CourseDetail;
        setCourse({
          ...payload,
          holes: (payload.holes ?? []).slice().sort((a, b) => a.number - b.number),
        });
      } catch (err) {
        setError(err instanceof Error ? err.message : t('courseDetails.errorLoad'));
      } finally {
        setLoading(false);
      }
    };

    if (isNavigationReady && user) {
      fetchCourse().catch(() => {});
    }
  }, [apiBaseUrl, courseId, isNavigationReady, t, user]);

  if (!isNavigationReady) {
    return null;
  }

  if (!user) {
    return <Redirect href="/" />;
  }

  return (
    <ScrollView style={styles.scroll} contentContainerStyle={styles.container}>
      <View style={styles.headerRow}>
        <Pressable style={styles.backButton} onPress={() => router.back()}>
          <Text style={styles.backText}>←</Text>
        </Pressable>
        <Text style={styles.title}>{t('courseDetails.title')}</Text>
      </View>

      {loading ? <Text style={styles.subtitle}>{t('courseDetails.loading')}</Text> : null}
      {error ? <Text style={styles.error}>{error}</Text> : null}

      {course ? (
        <View style={styles.card} lightColor={appColors.background.panel} darkColor={appColors.background.panel}>
          <Text style={styles.courseName}>{course.name}</Text>
          <Text style={styles.courseAddress}>{course.address}</Text>
          <View style={styles.metaRow}>
            <Text style={styles.meta}>{t('courseDetails.lat', { value: course.lat.toFixed(5) })}</Text>
            <Text style={styles.meta}>{t('courseDetails.lon', { value: course.lon.toFixed(5) })}</Text>
          </View>
          {course.source ? <Text style={styles.meta}>{t('courseDetails.source', { source: course.source })}</Text> : null}
          {course.holes && course.holes.length > 0 ? (
            <View style={styles.holesBlock}>
              <Text style={styles.sectionTitle}>{t('courseDetails.holesTitle')}</Text>
              {course.holes.map((hole) => (
                <View key={hole.id} style={styles.holeRow}>
                  <Text style={styles.holeNumber}>{t('courseDetails.hole', { number: hole.number })}</Text>
                  <View style={styles.holeTags}>
                    <Text style={styles.tag}>{t('courseDetails.par', { par: hole.par })}</Text>
                    <Text style={styles.tag}>{t('courseDetails.strokeIndex', { index: hole.strokeIndex })}</Text>
                    {hole.displayLengthMeters != null ? (
                      <Text style={styles.tag}>{formatHoleDistance(hole.displayLengthMeters, distanceUnit)}</Text>
                    ) : null}
                  </View>
                </View>
              ))}
            </View>
          ) : (
            <Text style={styles.subtitle}>{t('courseDetails.noHoles')}</Text>
          )}
        </View>
      ) : null}

      {/* Admin only. Reports whether imagery analysis has run for this course and what it
          found — the state that otherwise has to be pieced together from a Pulumi config
          value, a database row and a startup log line. */}
      {isAdmin && course ? (
        <CourseAnalysisStatusPanel
          apiBaseUrl={apiBaseUrl}
          token={user?.token}
          courseId={course.id}
        />
      ) : null}
    </ScrollView>
  );
}

const cardShadow = Platform.select({
  web: { boxShadow: '0px 2px 6px rgba(0,0,0,0.2)' },
  default: {
    shadowColor: appColors.text.secondary,
    shadowOpacity: 0.2,
    shadowRadius: 5,
    shadowOffset: { width: 0, height: 2 },
  },
});

const styles = StyleSheet.create({
  scroll: {
    backgroundColor: appColors.background.app,
  },
  container: {
    flexGrow: 1,
    padding: 16,
    gap: 12,
    backgroundColor: appColors.background.app,
  },
  headerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  backButton: {
    paddingVertical: 8,
    paddingHorizontal: 10,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: appColors.border.accent,
    backgroundColor: appColors.background.panel,
  },
  backText: {
    color: appColors.text.primary,
    fontWeight: '700',
    fontSize: 16,
  },
  title: {
    fontSize: 22,
    fontWeight: '700',
    color: appColors.text.primary,
  },
  subtitle: {
    color: appColors.text.primary,
  },
  error: {
    color: appColors.status.errorText,
  },
  card: {
    padding: 14,
    borderRadius: 14,
    borderWidth: 1,
    borderColor: appColors.border.strong,
    backgroundColor: appColors.background.panel,
    gap: 10,
    ...cardShadow,
  },
  courseName: {
    fontSize: 18,
    fontWeight: '800',
    color: appColors.text.primary,
  },
  courseAddress: {
    color: appColors.text.primary,
  },
  metaRow: {
    flexDirection: 'row',
    gap: 12,
    flexWrap: 'wrap',
  },
  meta: {
    color: appColors.accent.primary,
    fontSize: 13,
  },
  holesBlock: {
    gap: 8,
    marginTop: 4,
  },
  sectionTitle: {
    fontWeight: '700',
    color: appColors.text.primary,
  },
  holeRow: {
    paddingVertical: 8,
    borderBottomWidth: 1,
    borderBottomColor: appColors.border.strong,
    gap: 4,
  },
  holeNumber: {
    color: appColors.text.primary,
    fontWeight: '700',
  },
  holeTags: {
    flexDirection: 'row',
    gap: 8,
    flexWrap: 'wrap',
  },
  tag: {
    paddingVertical: 4,
    paddingHorizontal: 8,
    borderRadius: 8,
    backgroundColor: appColors.border.strong,
    borderWidth: 1,
    borderColor: appColors.border.strong,
    color: appColors.text.primary,
    fontSize: 12,
    fontWeight: '700',
  },
});
